import axios from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'

import { LvApiResponse, LvGeometryApiResponse } from '../../common/lvTypes'
import { useAppSelector } from '../../redux/hooks'
import { fixInvalidGeometryJson } from './useFetchPropertyLvGeometry'

/**
 * Fetches LV data and geometry from an API upon initialization
 * and when input parameters change. It maintains the request state and errors
 * during fetching.
 *
 * @param lvCode - Kod katastralniho uzemi (must be a 1-6 digit)
 * @param lvNumber - Cislo LV (must be a 1-6 digit)
 *
 * @returns Object containing:
 *  - fetching: boolean
 *  - error: boolean
 *  - data: object containing lvData and geometryData (both may be undefined)
 *
 * @example
 * // Basic usage
 * const { fetching, error, data } = useFetchLvDrawerData('123456', '21');
 *
 * // Use the data
 * const { lvData, geometryData } = data;
 */

export const useFetchLvDetailData = (lvCode?: string, lvNumber?: string) => {
  const token = useAppSelector(state => state.myProfile.token)
  const [fetching, setFetching] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [data, setData] = useState<{
    lvData: LvApiResponse | undefined
    geometryData: LvGeometryApiResponse | undefined
  }>({
    lvData: undefined,
    geometryData: undefined,
  })

  const prevLvCodeRef = useRef<string | undefined>(undefined)
  const prevLvNumberRef = useRef<string | undefined>(undefined)
  const isFetchingRef = useRef(false)

  const fetchData = useCallback(async () => {
    if (isFetchingRef.current) return

    isFetchingRef.current = true
    setFetching(true)
    setError(false)

    const apiUrlLv = `https://data.regesta.cz/DrmApi/api/v1.0/LV/${lvCode}/${lvNumber}?access_token=${token}&vcetneHistorie=true`
    const apiUrlGeometry = `https://data.regesta.cz/DrmApi/api/v1.0/LV/${lvCode}/${lvNumber}/Nemovitosti/Geometrie?access_token=${token}`

    try {
      const [responseLv, responseGeometry] = await Promise.all([
        axios.get(apiUrlLv),
        axios.get(apiUrlGeometry),
      ])

      const lvResponse: LvApiResponse = responseLv.data
      const geometry: LvGeometryApiResponse =
        typeof responseGeometry.data === 'string'
          ? JSON.parse(fixInvalidGeometryJson(responseGeometry.data))
          : responseGeometry.data

      const hasProperties =
        !!lvResponse?.nemovitosti?.budovy?.length ||
        !!lvResponse?.nemovitosti?.parcely?.length ||
        !!lvResponse?.nemovitosti?.jednotky?.length

      if (!hasProperties) {
        setError(true)
      } else {
        setData({
          lvData: lvResponse,
          geometryData: geometry,
        })
      }
    } catch (error) {
      setError(true)
    } finally {
      setFetching(false)

      isFetchingRef.current = false
      prevLvCodeRef.current = lvCode
      prevLvNumberRef.current = lvNumber
    }
  }, [lvCode, lvNumber, token])

  useEffect(() => {
    if (isFetchingRef.current || !lvCode || !lvNumber) {
      return
    }

    const isValid = /^\d{1,6}$/.test(lvCode) && /^\d{1,6}$/.test(lvNumber)
    const hasChanged =
      prevLvCodeRef.current !== lvCode || prevLvNumberRef.current !== lvNumber

    if (isValid && hasChanged) {
      fetchData()
    } else if (!isValid) {
      setError(true)
    }
  }, [fetchData, lvCode, lvNumber])

  return {
    fetching,
    error,
    data,
  }
}
